import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext'
import { useAuth } from 'contexts/AuthContext'
import { useSettingsRestrictions } from 'team/hooks/useSettingsRestrictions'
import { useTeam } from 'team/contexts/teamContext'
import { useTeamMember } from 'team/contexts/teamMemberContext'

/**
 * Custom hook to manage leads (contacts)
 * @returns {Object} Leads state and handlers
 */
export const useLeads = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { authState } = useAuth()
    const { currentTeamId } = useTeam()
    const { getMembers, teamMembers } = useTeamMember()
    const {
        contacts,
        isLoadingContacts,
        fetchContacts,
        addContact,
        saveLead: contextSaveLead
    } = useLeadCaptureContext()

    const [view, setView] = useState('list') // 'list', 'csv-import', 'lead-details'
    const [selectedLead, setSelectedLead] = useState(null)
    const [selectedLeadIds, setSelectedLeadIds] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [dateRange, setDateRange] = useState([
        new Date(new Date().getFullYear(), 0, 1), // Start of current year
        new Date(new Date().setHours(23, 59, 59, 999)) // End of today
    ])
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    })
    const [filters, setFilters] = useState({
        subteams: [],
        members: [],
        removedMembers: false,
        leadType: []
    })
    const [filterSearch, setFilterSearch] = useState('')

    const getIdString = (value) => {
        if (!value) return null
        if (typeof value === 'string') return value
        if (value._id) return String(value._id)
        if (value.userId) return String(value.userId)
        if (value.id) return String(value.id)
        if (value.toString && typeof value.toString === 'function') return value.toString()
        return null
    }

    // Settings restrictions
    const { isRestricted } = useSettingsRestrictions()

    // Detect route and set view accordingly
    useEffect(() => {
        if (location.pathname === '/team/leads/import-options') {
            setView('csv-import')
        } else if (location.pathname === '/team/leads') {
            setView('list')
        }
    }, [location.pathname])

    // Load contacts when component mounts
    useEffect(() => {
        if (authState?.user?.userId) {
            fetchContacts(authState.user.userId)
        }
    }, [authState?.user?.userId, fetchContacts])

    // Fetch team members for subteam filtering
    useEffect(() => {
        if (currentTeamId) {
            getMembers(currentTeamId, { status: 'active' })
        }
    }, [currentTeamId, getMembers])

    // Create mapping of userId/cardId to subteamIds for filtering
    const memberSubteamMap = useMemo(() => {
        if (!currentTeamId || !teamMembers[currentTeamId]) {
            console.log('🔍 No team members data available', { currentTeamId, teamMembers })
            return {}
        }

        const members = teamMembers[currentTeamId].members || []
        const map = {}

        console.log('🔍 Building member subteam map from', members.length, 'members')

        members.forEach(member => {
            const userId = getIdString(member.userId || member.user?.userId || member.user?._id)
            const subteamIds = member.subteamIds || []

            // Helper to extract subteam ID (handles both ObjectId and populated object)
            const getSubteamIdString = (subteam) => {
                if (!subteam) return null
                if (typeof subteam === 'string') return subteam
                // Handle populated subteam object (has _id, name, description)
                if (subteam._id) {
                    return subteam._id.toString()
                }
                // Handle ObjectId directly
                if (subteam.toString && typeof subteam.toString === 'function') {
                    return subteam.toString()
                }
                return null
            }

            const subteamIdStrings = subteamIds
                .map(getSubteamIdString)
                .filter(Boolean)

            if (userId && subteamIdStrings.length > 0) {
                map[userId] = subteamIdStrings
                console.log('🔍 Mapped userId', userId, 'to subteams', subteamIdStrings)
            }

            // Also map by cardId if defaultCard exists
            if (member.defaultCard?._id) {
                const cardId = getIdString(member.defaultCard?._id || member.defaultCard?.id || member.defaultCard)
                if (subteamIdStrings.length > 0) {
                    map[cardId] = subteamIdStrings
                    console.log('🔍 Mapped cardId', cardId, 'to subteams', subteamIdStrings)
                }
            }
        })

        console.log('🔍 Final Member Subteam Map:', map)
        return map
    }, [currentTeamId, teamMembers])

    // Filter leads based on search query, date range, and filters
    const filteredLeads = useMemo(() => {
        return Array.isArray(contacts)
            ? contacts.filter(contact => {
                // First filter by search query
                const query = searchQuery.toLowerCase()
                const matchesSearch = (
                    (contact.leadName && contact.leadName.toLowerCase().includes(query)) ||
                    (contact.leadEmail && contact.leadEmail.toLowerCase().includes(query)) ||
                    (contact.leadCompany && contact.leadCompany.toLowerCase().includes(query))
                )

                // Then filter by date range
                let matchesDateRange = true
                if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
                    const leadDate = contact.createdAt ? new Date(contact.createdAt) : null
                    if (leadDate) {
                        const startDate = new Date(dateRange[0])
                        startDate.setHours(0, 0, 0, 0)
                        const endDate = new Date(dateRange[1])
                        endDate.setHours(23, 59, 59, 999)
                        matchesDateRange = leadDate >= startDate && leadDate <= endDate
                    } else {
                        matchesDateRange = false
                    }
                }

                // Filter by members (if any selected)
                let matchesMemberFilter = true
                if (filters.members && filters.members.length > 0) {
                    // Check if the lead's userId or cardId matches any selected member
                    // filters.members contains TeamMember._id values
                    if (!currentTeamId || !teamMembers[currentTeamId]) {
                        matchesMemberFilter = false
                    } else {
                        const members = teamMembers[currentTeamId].members || []
                        const selectedMemberIds = filters.members
                            .map(id => getIdString(id))
                            .filter(Boolean)

                        // Find if any selected member has the same userId or cardId as the lead
                        matchesMemberFilter = members.some(member => {
                            const memberId = getIdString(member._id || member.id)
                            if (!selectedMemberIds.includes(memberId)) return false

                            const memberUserId = getIdString(member.userId || member.user?.userId || member.user?._id)
                            const memberCardId = getIdString(member.defaultCard?._id || member.defaultCard?.id)
                            const leadUserId = getIdString(contact.userId || contact.user?.userId || contact.user?._id)
                            const leadCardId = getIdString(contact.cardId || contact.card?._id || contact.card?.id)

                            if (leadCardId) {
                                return memberCardId && memberCardId === leadCardId
                            }
                            if (leadUserId) {
                                return memberUserId && memberUserId === leadUserId
                            }
                            return false
                        })
                    }
                }

                // Filter by lead type (if any selected)
                let matchesLeadType = true
                if (filters.leadType && filters.leadType.length > 0) {
                    const leadSource = contact.leadSource || 'manually-added'
                    matchesLeadType = filters.leadType.includes(leadSource)
                }

                // Filter by subteams (if any selected)
                let matchesSubteamFilter = true
                if (filters.subteams && filters.subteams.length > 0) {
                    const selectedSubteamIds = filters.subteams.map(id => {
                        if (typeof id === 'string') return id
                        if (id && id._id) return String(id._id) // Handle populated object
                        if (id && id.toString) return id.toString()
                        return String(id)
                    })

                    // Get subteamIds for this lead's userId or cardId
                    const leadUserId = getIdString(contact.userId || contact.user?.userId || contact.user?._id)
                    const leadCardId = getIdString(contact.cardId || contact.card?._id || contact.card?.id)

                    // Try to get subteamIds from map using userId or cardId
                    const leadSubteamIds = (leadCardId && memberSubteamMap[leadCardId])
                        ? memberSubteamMap[leadCardId]
                        : (leadUserId && memberSubteamMap[leadUserId])
                            ? memberSubteamMap[leadUserId]
                            : []

                    // Convert selected subteam IDs to strings for comparison
                    // Check if any of the lead's subteams match the selected subteams
                    matchesSubteamFilter = leadSubteamIds.length > 0 &&
                        leadSubteamIds.some(subteamId => selectedSubteamIds.includes(String(subteamId)))

                    // Debug logging for troubleshooting
                    console.log('🔍 Subteam filter check:', {
                        leadId: contact._id,
                        leadName: contact.leadName,
                        leadUserId,
                        leadCardId,
                        leadSubteamIds,
                        selectedSubteamIds,
                        matches: matchesSubteamFilter,
                        mapKeys: Object.keys(memberSubteamMap)
                    })
                }

                return matchesSearch && matchesDateRange && matchesMemberFilter && matchesLeadType && matchesSubteamFilter
            })
            : []
    }, [contacts, searchQuery, dateRange, filters, memberSubteamMap])

    // Use filtered leads
    const displayLeads = filteredLeads

    // Get selected leads based on selected IDs
    const getSelectedLeads = () => {
        if (!selectedLeadIds.length) return filteredLeads // If none selected, export all
        return filteredLeads.filter(lead => selectedLeadIds.includes(lead._id))
    }

    const handleRefresh = () => {
        if (authState?.user?.userId) {
            fetchContacts(authState.user.userId)
        }
    }

    const handleAddLeads = () => {
        console.log('Adding leads...')
        // This will be handled by the menu component
    }

    const handleExport = (exportType) => {
        // Check restrictions
        if (exportType === 'csv' && isRestricted('Downloading leads (CSV)')) {
            setSnackbar({
                open: true,
                message: 'Downloading leads as CSV is restricted by your subteam.',
                severity: 'warning'
            })
            return
        }
        if (exportType === 'vcf' && isRestricted('Downloading leads as contacts (.vcf)')) {
            setSnackbar({
                open: true,
                message: 'Downloading leads as contacts (.vcf) is restricted by your subteam.',
                severity: 'warning'
            })
            return
        }
        if (exportType === 'crm' && isRestricted('Exporting Leads to CRM')) {
            setSnackbar({
                open: true,
                message: 'Exporting leads to CRM is restricted by your subteam.',
                severity: 'warning'
            })
            return
        }

        // Handle export functionality
        console.log('Export leads:', getSelectedLeads(), exportType)
    }

    const handleImportCSV = () => {
        setView('csv-import')
        navigate('/team/leads/import-options')
    }

    const handleLeadClick = (lead, goToEditMode = false) => {
        setSelectedLead({
            lead: lead,
            startInEditMode: goToEditMode
        })
        setView('lead-details')
    }

    const handleBackToList = () => {
        setView('list')
        setSelectedLead(null)
        navigate('/team/leads')
    }

    const handleSelectLeads = (selectedIds) => {
        setSelectedLeadIds(selectedIds)
    }

    const handleDateRangeChange = (startDate, endDate) => {
        // Ensure dates are Date objects
        const start = startDate instanceof Date ? startDate : new Date(startDate)
        const end = endDate instanceof Date ? endDate : new Date(endDate)
        setDateRange([start, end])
    }

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value)
    }

    const handleNotification = (notification) => {
        setSnackbar(notification)
    }

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }))
    }

    const handleDeleteComplete = () => {
        setView('list')
        setSelectedLead(null)
        handleRefresh()
    }

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }))
    }

    const handleFilterSearchChange = (searchValue) => {
        setFilterSearch(searchValue)
    }

    const handleClearFilters = () => {
        setFilters({
            subteams: [],
            members: [],
            removedMembers: false,
            leadType: []
        })
        setFilterSearch('')
    }

    return {
        view,
        selectedLead,
        selectedLeadIds,
        searchQuery,
        dateRange,
        snackbar,
        filteredLeads,
        displayLeads,
        isLoadingContacts,
        isRestricted,
        getSelectedLeads,
        handleRefresh,
        handleAddLeads,
        handleExport,
        handleImportCSV,
        handleLeadClick,
        handleBackToList,
        handleSelectLeads,
        handleDateRangeChange,
        handleSearchChange,
        handleNotification,
        handleSnackbarClose,
        handleDeleteComplete,
        filters,
        filterSearch,
        handleFilterChange,
        handleFilterSearchChange,
        handleClearFilters
    }
}

